import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { TProject } from "@/zapi-frontend/stores/project";
import { getRouteProjectDetails, getRouteCreateProject, getRouteNewVersion, getRouteReviewRelaunch } from "@/zapi-frontend/shared/const/router";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import type { TSystemColumn } from "../lib/portfolioStorage";
import { ProjectCard } from "./ProjectListRedesign/_ProjectCard";

const CARDS_PAGE_SIZE = 20;

type TProps = {
  projects: TProject[];
  dimensions: TOrganizationProjectDimension[];
  hiddenDimensionIds: Set<string>;
  hiddenSystemColumns: Set<TSystemColumn>;
  hasEdit: boolean;
  canDuplicateProject: boolean;
  organizationId: number;
  onListInvalidated: () => void;
  onDuplicateProject: (id: string) => void;
  onDeleteProject: (id: string) => void;
  deletingProjectId: string | null;
};

export function ProjectsCards({
  projects,
  dimensions,
  hiddenDimensionIds,
  hiddenSystemColumns,
  hasEdit,
  canDuplicateProject,
  organizationId,
  onListInvalidated,
  onDuplicateProject,
  onDeleteProject,
  deletingProjectId
}: TProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const visibleDimensions = dimensions.filter((d) => !hiddenDimensionIds.has(String(d.id)));

  const [visibleCount, setVisibleCount] = useState(CARDS_PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const listKey = useMemo(() => projects.map((p) => p.id).join(","), [projects]);

  useEffect(() => {
    setVisibleCount(CARDS_PAGE_SIZE);
  }, [listKey]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((current) => Math.min(current + CARDS_PAGE_SIZE, projects.length));
        }
      },
      { rootMargin: "600px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [projects.length, visibleCount]);

  const renderedProjects = projects.slice(0, visibleCount);

  return (
    <div className="flex flex-col gap-2">
      {renderedProjects.map((project) => {
        const onOpenProject = (versionId?: string) => {
          if (versionId && versionId !== project.id) {
            const version = project.versions?.find((v) => v.id === versionId);
            navigate(version?.isDraft ? getRouteNewVersion(versionId) : getRouteProjectDetails(versionId));
            return;
          }
          const isLegacyDraft = project.id.startsWith("draft-");
          if (isLegacyDraft) {
            const briefId = project.caseBriefId ?? Number(project.id.slice("draft-".length));
            if (Number.isFinite(briefId)) {
              navigate(`${getRouteCreateProject()}?caseBriefId=${briefId}`);
              return;
            }
          }
          if (project.isRelaunchPending) {
            navigate(getRouteReviewRelaunch(project.id));
            return;
          }
          if (project.isDraft && project.versionGroupId) {
            navigate(getRouteNewVersion(project.id));
            return;
          }
          if (project.caseBriefId) {
            navigate(`${getRouteCreateProject()}?caseBriefId=${project.caseBriefId}`);
            return;
          }
          navigate(getRouteProjectDetails(project.id));
        };

        return (
          <ProjectCard
            key={project.id}
            project={project}
            visibleDimensions={visibleDimensions}
            hiddenSystemColumns={hiddenSystemColumns}
            hasEdit={hasEdit}
            canDuplicateProject={canDuplicateProject}
            organizationId={organizationId}
            onListInvalidated={onListInvalidated}
            onOpenProject={onOpenProject}
            onDuplicateProject={onDuplicateProject}
            onDeleteProject={onDeleteProject}
            isDeleting={deletingProjectId === project.id}
          />
        );
      })}
      {visibleCount < projects.length && (
        <div ref={sentinelRef} className="flex h-9 shrink-0 items-center justify-center">
          <Spinner aria-label={t("loading")} className="size-4 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
